import { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, NavLink } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { useBranding } from '@/contexts/BrandingContext';
import { useLanguage } from '@/i18n/LanguageContext';

// Components
import StaffDashboardHome from '@/components/staff/StaffDashboardHome';
import StaffReportsPage from '@/components/staff/StaffReportsPage';
import StaffSettingsPage from '@/components/staff/StaffSettingsPage';
import StaffSessionsPage from '@/components/staff/StaffSessionsPage';
import ManagerAttendancePage from '@/components/staff/ManagerAttendancePage';
import ManagerReportsPage from '@/components/staff/ManagerReportsPage';
import ManagerLeaveManagementPage from '@/components/staff/ManagerLeaveManagementPage';
import StaffLeaveRequestPage from '@/components/staff/StaffLeaveRequestPage';
import StaffNotificationsPage from '@/components/staff/StaffNotificationsPage';
import ThemeToggle from '@/components/ThemeToggle';

// UI Components
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  LayoutDashboard, 
  FileText, 
  LogOut, 
  Menu,
  X,
  Settings,
  AlertTriangle,
  Building2,
  Users,
  CalendarDays,
  ClipboardList,
  Bell
} from 'lucide-react';

function StaffDashboard() {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [showLogoutAlert, setShowLogoutAlert] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [leaveNotificationCount, setLeaveNotificationCount] = useState(0);
  const [teamLeavePendingCount, setTeamLeavePendingCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const prevNotificationCount = useRef(0);
  const notificationSoundRef = useRef(null);

  // ── Restore session token from localStorage on every mount ──────────────
  // Mobile browsers drop cookies when the app is backgrounded/killed.
  // We store the token in localStorage as a fallback and send it as a header.
  useEffect(() => {
    const token = localStorage.getItem('staff_session_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);
  
  // Initialize notification sound
  useEffect(() => {
    // Create a simple notification beep using AudioContext
    const createNotificationSound = () => {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = 800;
        oscillator.type = 'sine';
        gainNode.gain.value = 0.3;
        
        return { audioContext, oscillator, gainNode };
      } catch (e) {
        console.log('AudioContext not supported');
        return null;
      }
    };
    
    notificationSoundRef.current = createNotificationSound;
  }, []);
  
  // Function to play notification sound
  const playNotificationSound = () => {
    try {
      if (notificationSoundRef.current) {
        const sound = notificationSoundRef.current();
        if (sound) {
          sound.oscillator.start();
          sound.gainNode.gain.exponentialRampToValueAtTime(0.01, sound.audioContext.currentTime + 0.3);
          setTimeout(() => {
            sound.oscillator.stop();
            sound.audioContext.close();
          }, 300);
        }
      }
    } catch (e) {
      console.log('Could not play notification sound:', e);
    }
  };
  
  // Handle notification updates from keep-alive / notification poll
  const handleNotificationUpdate = async (newCount, newLeaveCount, { showToast = true } = {}) => {
    const prev = prevNotificationCount.current;
    if (showToast && newCount > prev) {
      playNotificationSound();
      try {
        const response = await axios.get(`${API}/staff/notifications`, { withCredentials: true });
        const list = response.data?.notifications || [];
        const latestUnread = list.find((n) => !n.is_read) || list[0];
        if (latestUnread?.message) {
          toast.info(latestUnread.sender_name ? `From ${latestUnread.sender_name}` : 'New notification', {
            description: latestUnread.message,
            duration: 8000,
          });
        } else {
          toast.info('You have a new notification', { duration: 5000 });
        }
      } catch {
        toast.info('You have a new notification', { duration: 5000 });
      }
    }
    prevNotificationCount.current = newCount;
    setNotificationCount(newCount);
    if (newLeaveCount !== undefined) {
      setLeaveNotificationCount(newLeaveCount);
    }
  };

  // Global keep-alive — runs on every staff page, not just dashboard
  const keepAliveIntervalRef = useRef(null);
  const notificationPollRef = useRef(null);
  const sendKeepAlive = async () => {
    try {
      const response = await axios.post(`${API}/staff/keep-alive`, {}, { withCredentials: true });
      if (response.data.notifications) {
        handleNotificationUpdate(
          response.data.notifications.unread_count || 0,
          response.data.notifications.leave_unread_count
        );
      }
    } catch (err) {
      // silent — keep-alive failures are non-critical
    }
  };

  // Dedicated notification poll (matches desktop: every 60s + on focus)
  const pollNotifications = async () => {
    try {
      const response = await axios.get(`${API}/staff/notifications/unread-count`, {
        withCredentials: true,
      });
      const count = response.data.unread_count || 0;
      await handleNotificationUpdate(count);
    } catch (err) {
      // silent
    }
  };

  useEffect(() => {
    fetchUser();
    checkAttendanceStatus();
    fetchLeaveNotifications();
    fetchNotificationCount();
    fetchTeamLeavePendingCount();

    // Send initial keep-alive immediately
    sendKeepAlive();

    // Keep-alive every 3 minutes (attendance heartbeat)
    keepAliveIntervalRef.current = setInterval(sendKeepAlive, 180000);

    // Notification badge poll every 60s (same cadence as desktop)
    notificationPollRef.current = setInterval(pollNotifications, 60 * 1000);

    // Re-send keep-alive + refresh notifications when tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        sendKeepAlive();
        pollNotifications();
      }
    };
    const handleWindowFocus = () => {
      pollNotifications();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      clearInterval(keepAliveIntervalRef.current);
      clearInterval(notificationPollRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);
  
  const fetchLeaveNotifications = async () => {
    try {
      const response = await axios.get(`${API}/staff/leave-notifications`, {
        withCredentials: true
      });
      setLeaveNotificationCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch leave notifications:', error);
    }
  };

  const fetchTeamLeavePendingCount = async () => {
    try {
      const response = await axios.get(`${API}/manager/leave-requests/pending-count`, {
        withCredentials: true
      });
      setTeamLeavePendingCount(response.data.pending_count || 0);
    } catch (error) {
      // Non-managers get 403 — ignore
      setTeamLeavePendingCount(0);
    }
  };

  const fetchNotificationCount = async () => {
    try {
      const response = await axios.get(`${API}/staff/notifications/unread-count`, {
        withCredentials: true
      });
      const count = response.data.unread_count || 0;
      prevNotificationCount.current = count;
      setNotificationCount(count);
    } catch (error) {
      console.error('Failed to fetch notification count:', error);
    }
  };
  
  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      // If 401 and we have a stored token, it's already set in the header — the
      // request failed even with it, so the token is truly expired. Clear and redirect.
      if (error.response?.status === 401) {
        localStorage.removeItem('staff_session_token');
        delete axios.defaults.headers.common['Authorization'];
        navigate('/login');
      }
      console.error('Failed to fetch user:', error);
    }
  };
  
  const checkAttendanceStatus = async () => {
    try {
      const response = await axios.get(`${API}/staff/dashboard/stats`, {
        withCredentials: true
      });
      // Check if user is checked in but not checked out
      const isCurrentlyCheckedIn = response.data.is_checked_in && !response.data.is_checked_out;
      setIsCheckedIn(isCurrentlyCheckedIn);
    } catch (error) {
      console.error('Failed to check attendance status:', error);
    }
  };
  
  const handleLogoutClick = () => {
    // Re-check attendance status before showing alert
    checkAttendanceStatus().then(() => {
      if (isCheckedIn) {
        setShowLogoutAlert(true);
      } else {
        performLogout();
      }
    });
  };
  
  const handleConfirmLogout = async () => {
    setLoggingOut(true);
    try {
      // First, check out the user
      if (isCheckedIn) {
        await axios.post(`${API}/staff/check-out`, {}, {
          withCredentials: true
        });
      }
      // Then logout
      await performLogout();
    } catch (error) {
      console.error('Check-out during logout failed:', error);
      // Still try to logout even if check-out fails
      await performLogout();
    }
  };
  
  const performLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      // Clear persisted session from localStorage
      localStorage.removeItem('staff_session_token');
      localStorage.removeItem('staff_type');
      delete axios.defaults.headers.common['Authorization'];
      setLoggingOut(false);
      setShowLogoutAlert(false);
      navigate('/login');
    }
  };
  
  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: '/dashboard/leave-requests', icon: CalendarDays, label: t('nav.leave_management'), badge: leaveNotificationCount },
    { path: '/dashboard/notifications', icon: Bell, label: t('nav.notifications'), badge: notificationCount },
    { path: '/dashboard/reports', icon: FileText, label: t('nav.reports') },
    { path: '/dashboard/settings', icon: Settings, label: t('nav.settings') },
  ];
  
  // Add Team Attendance + Team Leave menus for managers
  const staffType = user?.staff_settings?.staff_type || localStorage.getItem('staff_type') || 'staff';
  if (staffType === 'manager') {
    navItems.splice(1, 0,
      { path: '/dashboard/team-attendance', icon: Users, label: t('attendance.staff_attendance') },
      { path: '/dashboard/team-leave', icon: ClipboardList, label: 'Team Leave', badge: teamLeavePendingCount },
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-button"
        >
          {mobileMenuOpen ? <X /> : <Menu />}
        </Button>
      </div>
      
      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full bg-white border-r border-gray-200 z-40
        transition-all duration-300 ease-in-out
        ${sidebarOpen ? 'w-64' : 'w-20'}
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}
      data-testid="staff-sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-2">
              {branding.logo_url ? (
                <img 
                  src={branding.logo_url} 
                  alt={branding.platform_name} 
                  className={`transition-all ${sidebarOpen ? 'h-8' : 'h-6'} object-contain`}
                />
              ) : (
                <>
                  <Building2 className={`text-blue-600 ${sidebarOpen ? 'w-6 h-6' : 'w-5 h-5'}`} />
                  <h1 className={`font-bold text-blue-600 transition-all ${sidebarOpen ? 'text-lg' : 'hidden'}`}>
                    {branding.platform_name}
                  </h1>
                </>
              )}
            </div>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2" data-testid="staff-navigation">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/dashboard'}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                  ${isActive ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100'}
                `}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5" />
                {sidebarOpen && <span className="flex-1">{item.label}</span>}
                {item.badge > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
          
          {/* User Menu */}
          <div className="p-4 border-t border-gray-200 space-y-2">
            <div className={`flex items-center ${sidebarOpen ? 'justify-between' : 'justify-center'} px-1`}>
              {sidebarOpen && (
                <span className="text-xs font-medium text-gray-500">Theme</span>
              )}
              <ThemeToggle />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3"
                  data-testid="staff-user-menu-trigger"
                >
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback>{user?.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  {sidebarOpen && (
                    <div className="flex-1 text-left">
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{t('common.details')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogoutClick} data-testid="staff-logout-button">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className={`
        transition-all duration-300
        ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}
        p-8
      `}>
        <Routes>
          <Route path="/" element={<StaffDashboardHome onStatusChange={checkAttendanceStatus} onNotificationUpdate={handleNotificationUpdate} />} />
          <Route path="/team-attendance" element={<ManagerAttendancePage />} />
          <Route path="/team-leave" element={<ManagerLeaveManagementPage />} />
          <Route path="/leave-requests" element={<StaffLeaveRequestPage />} />
          <Route path="/notifications" element={<StaffNotificationsPage onCountChange={(count) => {
            prevNotificationCount.current = count;
            setNotificationCount(count);
          }} />} />
          <Route path="/reports" element={staffType === 'manager' ? <ManagerReportsPage /> : <StaffReportsPage />} />
          <Route path="/reports/sessions/:date" element={<StaffSessionsPage />} />
          <Route path="/settings" element={<StaffSettingsPage />} />
          <Route path="*" element={<StaffDashboardHome onStatusChange={checkAttendanceStatus} onNotificationUpdate={handleNotificationUpdate} />} />
        </Routes>
      </main>
      
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      
      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showLogoutAlert} onOpenChange={setShowLogoutAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              {t('attendance.check_in')} - {t('common.status')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600">
              {t('common.confirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loggingOut} data-testid="cancel-logout-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmLogout}
              disabled={loggingOut}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-logout-btn"
            >
              {loggingOut ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Checking out...
                </>
              ) : (
                'Yes, Checkout & Logout'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default StaffDashboard;
